import { useState, useEffect, useMemo } from "react";
import { apiFetch, can } from "../api";
import { Spinner, EmptyState } from "../components/ui";  // UI-SWEEP-DIRECTORY
import EditContactModal from "../components/EditContactModal";
import { SortableTh, sortRowsBy } from "../components/SortableTable";

// ============================================================================
// Contacts page (Chunk 7F / 7G)
// ----------------------------------------------------------------------------
// Master contact list.
//  - Admins see every contact (filterable by pending-review).
//  - Non-admins see only contacts linked via claim_parties to claims where
//    they are the lead or secondary adjuster. The role-aware filter lives in
//    GET /contacts on the backend so the frontend just asks the question.
//
// Edit flow: opens the shared EditContactModal. Non-admin edits flip the
// row to needs_admin_review=true and the nav badge re-fetches via the
// refreshReviewCount() callback threaded down from App.js.
//
// Chunk 7G: sortable column headers, sticky filter/header bar, and
// navigation callbacks to jump from linked-claim rows in the edit modal.
// ============================================================================

function parseErr(e, fallback) {
  try { return JSON.parse(e.message).detail || fallback; } catch { return fallback; }
}

function contactDisplayName(c) {
  return [c.first_name, c.last_name].filter(Boolean).join(" ") || "(unnamed)";
}

export default function Contacts({ currentUser, onReviewCountChanged, onNavigateToClaim, initialContactId, onClearInitial }) {
  // Chunk 14C: kept name for prop-pass stability; means "has approve_contacts".
  const isAdmin = can("approve_contacts");
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [search, setSearch] = useState("");
  const [pendingOnly, setPendingOnly] = useState(false);
  const [editing, setEditing] = useState(null);
  const [sort, setSort] = useState({ key: "last_name", dir: "asc" });

  const load = () => {
    setLoading(true);
    const q = new URLSearchParams();
    if (search.trim()) q.set("search", search.trim());
    if (pendingOnly) q.set("needs_review", "true");
    const qs = q.toString();
    apiFetch(`/contacts${qs ? "?" + qs : ""}`)
      .then(rows => { setContacts(rows); setLoading(false); })
      .catch(e => { setErr(parseErr(e, "Failed to load contacts")); setLoading(false); });
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [pendingOnly]);

  // Deep-link: if App.js navigated us here with an initialContactId, open
  // that contact's edit modal once the list loads (Chunk 7G click-through
  // from the Companies modal).
  useEffect(() => {
    if (!initialContactId || loading) return;
    const match = contacts.find(c => c.id === initialContactId);
    if (match) { setEditing(match); onClearInitial?.(); }
    else {
      // Row may be hidden by the current search filter — fall back to fetching
      // the single record directly. GET /contacts/{id} is role-gated server-side.
      apiFetch(`/contacts/${initialContactId}`).then(row => { setEditing(row); onClearInitial?.(); }).catch(() => onClearInitial?.());
    }
    // eslint-disable-next-line
  }, [initialContactId, loading, contacts]);

  const onSearchKey = (e) => { if (e.key === "Enter") load(); };

  const pendingCount = contacts.filter(c => c.needs_admin_review).length;

  const quickApprove = async (contact) => {
    if (!window.confirm(`Approve "${contactDisplayName(contact)}" as reviewed?`)) return;
    try {
      await apiFetch(`/contacts/${contact.id}/approve`, { method:"POST" });
      onReviewCountChanged?.();
      load();
    } catch (e) { alert(parseErr(e, "Failed to approve")); }
  };

  const sortAccessors = useMemo(() => ({
    display_name: c => [c.last_name, c.first_name].filter(Boolean).join(", "),
    phone_any:    c => c.phone || c.phone_mobile,
    status_rank:  c => c.needs_admin_review ? 1 : 0,
  }), []);

  const sorted = sortRowsBy(contacts, sort.key, sort.dir, sortAccessors);

  return (
    <div className="page list-view">
      <div className="list-header">
        <div className="section-header">
          <span className="section-title">
            Contacts ({contacts.length})
            {pendingCount > 0 && <span style={{marginLeft:10,fontSize:12,color:"#b45309",fontWeight:500}}>— {pendingCount} pending review</span>}
          </span>
        </div>

        {!isAdmin && (
          <div style={{background:"#eff6ff",border:"1px solid #93c5fd",borderRadius:6,padding:"10px 12px",marginBottom:12,fontSize:12,color:"#1e40af"}}>
            Showing only contacts linked to claims where you are the lead or secondary adjuster.
          </div>
        )}

        <div style={{display:"flex",gap:12,alignItems:"center",marginBottom:12,flexWrap:"wrap"}}>
          <input value={search} onChange={e=>setSearch(e.target.value)} onKeyDown={onSearchKey} placeholder="Search by name or email... (Enter)" style={{flex:"1 1 240px",maxWidth:400}}/>
          <button className="btn btn-outline btn-sm" onClick={load}>Search</button>
          {isAdmin && (
            <label style={{display:"flex",alignItems:"center",gap:6,fontSize:12,color:"var(--text2)",cursor:"pointer"}}>
              <input type="checkbox" checked={pendingOnly} onChange={e=>setPendingOnly(e.target.checked)}/>
              Pending review only
            </label>
          )}
        </div>

        {err && <div className="alert-error">{err}</div>}
      </div>

      <div className="list-body card table-wrap">
        {loading ? <Spinner label="Loading contacts…" />
         : sorted.length === 0 ? <EmptyState icon="👥" title="No contacts match" hint="Try a different search or clear filters." />
         : (<table>
            <thead><tr>
              <SortableTh label="Name"    col="display_name" sort={sort} setSort={setSort}/>
              <SortableTh label="Title"   col="title"        sort={sort} setSort={setSort}/>
              <SortableTh label="Company" col="company_name" sort={sort} setSort={setSort}/>
              <SortableTh label="Email"   col="email"        sort={sort} setSort={setSort}/>
              <SortableTh label="Phone"   col="phone_any"    sort={sort} setSort={setSort}/>
              {isAdmin && <SortableTh label="Status" col="status_rank" sort={sort} setSort={setSort}/>}
              <th className="text-right">Actions</th>
            </tr></thead>
            <tbody>{sorted.map(c => (
              <tr key={c.id} style={c.needs_admin_review ? {background:"#fffbeb"} : undefined}>
                <td style={{fontWeight:600}}>{contactDisplayName(c)}</td>
                <td>{c.title || <span style={{color:"var(--text3)"}}>—</span>}</td>
                <td>{c.company_name || <span style={{color:"var(--text3)"}}>—</span>}</td>
                <td>{c.email || <span style={{color:"var(--text3)"}}>—</span>}</td>
                <td>{c.phone || c.phone_mobile || <span style={{color:"var(--text3)"}}>—</span>}</td>
                {isAdmin && (
                  <td>
                    {c.needs_admin_review
                      ? <span className="badge" style={{background:"#fef3c7",color:"#92400e"}}>Pending Review</span>
                      : <span className="badge" style={{background:"#d1fae5",color:"#065f46"}}>Active</span>}
                  </td>
                )}
                <td className="text-right" style={{whiteSpace:"nowrap"}}>
                  {isAdmin && c.needs_admin_review && (
                    <button className="btn btn-primary btn-sm" onClick={() => quickApprove(c)} style={{marginRight:4}}>Approve</button>
                  )}
                  <button className="btn btn-outline btn-sm" onClick={() => setEditing(c)}>Edit</button>
                </td>
              </tr>
            ))}</tbody>
          </table>)}
      </div>

      {editing && <EditContactModal
        contact={editing}
        isAdmin={isAdmin}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); onReviewCountChanged?.(); load(); }}
        onNavigateToClaim={onNavigateToClaim}
      />}
    </div>
  );
}
